import { FC, useEffect } from "react";
import { Button, Col, Form, FormProps, Input, Modal, Row, Select } from "antd";
import { TCommunication } from "~/types";
import { useCreateCommunication } from "~/services/communication";
import { useNotification } from "~/hooks";
import { useDetailBookingStore } from "../../detail-booking.store";
import { useGetTemplates } from "~/services/template";
import { capitalize } from "~/utils";
import { useQueryClient } from "@tanstack/react-query";
import { BOOKING_KEYS } from "~/services/booking";
import { PreferredCommunicationMethod } from "~/utils/constants";
import { RecipientSelector } from "./recipient-selector";

type Props = {
  communication: TCommunication | null;
  onClose: () => void;
};

const MAX_REPLY_LENGTH = 2000;

export const ReplyModal: FC<Props> = ({ communication, onClose }) => {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const { success, error } = useNotification();
  const { booking } = useDetailBookingStore();
  const { data: templates, isLoading } = useGetTemplates();
  const mutation = useCreateCommunication();

  // Handle template selection
  const handleTemplateChange = (templateId: string | undefined) => {
    if (!templateId) return;

    const selectedTemplate = templates?.items.find((t) => t.id === templateId);
    if (selectedTemplate) {
      form.setFieldsValue({
        body: selectedTemplate.body || form.getFieldValue('body'),
      });
    }
  };

  // Auto-populate reply fields when communication changes
  useEffect(() => {
    if (communication) {
      // Determine recipient email
      let replyToEmail = '';
      if (communication.isReply && communication.replyFromEmail) {
        // Replying to a reply - send to the person who replied
        replyToEmail = communication.replyFromEmail;
      } else if (communication.recipientEmails && communication.recipientEmails.length > 0) {
        // Replying to original - send to first recipient
        replyToEmail = communication.recipientEmails[0];
      }

      form.setFieldsValue({
        subject: communication.subject?.startsWith('Re: ')
          ? communication.subject
          : `Re: ${communication.subject || 'Your message'}`,
        replyToEmail,
        body: '',
      });
    }
  }, [communication, form]);

  const onSubmit: FormProps["onFinish"] = (values) => {
    if (!booking || !communication) return;

    // Extract CC and BCC emails
    const ccEmails = values.ccRecipients?.additionalEmails || [];
    const bccEmails = values.bccRecipients?.additionalEmails || [];

    const payload = {
      name: values.subject || 'Reply',
      subject: values.subject,
      body: values.body,
      templateId: values.templateId,
      jobTicketId: booking.id,
      channels: PreferredCommunicationMethod.EMAIL,
      additionalEmails: [values.replyToEmail],
      cc: ccEmails.length > 0 ? ccEmails : undefined,
      bcc: bccEmails.length > 0 ? bccEmails : undefined,
      // Threading information
      parentCommunicationId: communication.id,
      inReplyTo: communication.messageId,
      emailThreadId: communication.emailThreadId,
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        success({
          message: "Success",
          description: "Reply sent successfully.",
        });
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(booking.id),
        });
        form.resetFields();
        onClose();
      },
      onError: (err) => {
        error({
          message: "Error",
          description: err.message ?? "Failed to send reply.",
        });
      },
    });
  };

  if (!communication) return null;

  const recipientName = communication.isReply
    ? (communication.replyFromName || communication.replyFromEmail)
    : (communication.recipientEmails?.[0] || 'Recipient');

  return (
    <Modal
      title={`Reply to ${recipientName}`}
      open={!!communication}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        className="mt-4"
      >
        {/* Recipients Row: TO, CC, BCC */}
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="replyToEmail"
              label={<span className="font-semibold">TO</span>}
              className="mb-0"
              rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input type="email" placeholder="recipient@example.com" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="ccRecipients"
              label={<span className="font-semibold">CC</span>}
              className="mb-0"
            >
              <RecipientSelector />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="bccRecipients"
              label={<span className="font-semibold">BCC</span>}
              className="mb-0"
            >
              <RecipientSelector />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="templateId"
          label="Template (Auto-fills)"
          className="mt-4"
        >
          <Select
            loading={isLoading}
            placeholder="Select template (optional)"
            allowClear
            onChange={handleTemplateChange}
            options={templates?.items.map((temp) => ({
              label: capitalize(temp.name),
              value: temp.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="subject"
          label="Subject"
          rules={[{ required: true, message: 'Please enter a subject' }]}
        >
          <Input type="text" placeholder="Re: ..." />
        </Form.Item>

        <Form.Item
          name="body"
          label={`Message (max ${MAX_REPLY_LENGTH} characters)`}
          rules={[
            { required: true, message: 'Please enter a message' },
            { max: MAX_REPLY_LENGTH, message: `Message cannot exceed ${MAX_REPLY_LENGTH} characters` }
          ]}
        >
          <Input.TextArea
            rows={6}
            placeholder="Type your reply here..."
            maxLength={MAX_REPLY_LENGTH}
            showCount
          />
        </Form.Item>

        <div className="flex justify-end space-x-2">
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            Send Reply
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
